import axios from "axios";
import { API_BASE_URL } from "../config/apiConfig";
import DjehootyDocument from "./document";
import Thumbnail, { resolveThumbnailPath, resolveThumbnailPathFromCategory, ThumbnailCategory, ThumbnailStatus, WithThumbnail } from "./thumbnail";

/**
 * Interface representing the updatable fields of an Individual
 */
export interface IndividualUpdateData {
    firstname?: string;
    lastname?: string;
    occupation?: string;
    wikipediaUrl?: string;
    wikidataId?: string;
}

export default class Individual implements WithThumbnail {
    id: number;
    firstname: string;
    lastname: string;
    occupation: string;
    wikipediaUrl: string;
    wikidataId: string;
    thumbnail: Thumbnail | undefined;

    constructor(
        id: number = 0,
        firstname: string = "",
        lastname: string = "",
        occupation: string = "",
        wikipediaURL: string = "",
        wikidataID: string = "",
        thumbnail: Thumbnail | undefined = undefined
    ) {
        this.id = id;
        this.firstname = firstname;
        this.lastname = lastname;
        this.occupation = occupation;
        this.wikipediaUrl = wikipediaURL;
        this.wikidataId = wikidataID;
        this.thumbnail = thumbnail;
    }

    // TODO : it seems a bit dirty to me, to be checked
    loadFromJson(jsonPayload: { id: number; firstname: string; lastname: string; occupation: string; wikipedia_url: string; wikidata_id: string; thumbnail: { file_thumbnail_id: number; url: string; status: ThumbnailStatus }; }) {
        this.id = jsonPayload.id;
        this.firstname = jsonPayload.firstname;
        this.lastname = jsonPayload.lastname;
        this.occupation = jsonPayload.occupation;
        this.wikipediaUrl = jsonPayload.wikipedia_url;
        this.wikidataId = jsonPayload.wikidata_id;
        if (jsonPayload.thumbnail) {
            this.thumbnail = new Thumbnail(jsonPayload.thumbnail.file_thumbnail_id, jsonPayload.thumbnail.url, jsonPayload.thumbnail.status);
        }
    }

    getFullname = (): string => {
        return this.firstname + " " + this.lastname;
    }

    /**
     * Gets the thumbnail status of the individual
     * @returns ThumbnailStatus
     */
    getThumbnailStatus = (): ThumbnailStatus => {
        if (!this.thumbnail) {
            return "none" as ThumbnailStatus;
        } else {
            return this.thumbnail.status;
        }
    }

    /**
     * Gets the thumbnail path of the individual
     * @returns string - The thumbnail path
     */
    getThumbnailPath = (): string => {
        try {
            if (!this.thumbnail) {
                return resolveThumbnailPathFromCategory(ThumbnailCategory.Individual);
            } else {
                return resolveThumbnailPath(this.thumbnail.status, this.thumbnail.url, ThumbnailCategory.Individual);
            }
        } catch (error) {
            console.log("Error : getThumbnailPath", error);
            return resolveThumbnailPathFromCategory(ThumbnailCategory.Individual);
        }
    }

    /**
     * Fetches a list of all documents where individual is identified
     * @returns Promise<DjehootyDocument[]>
     */
    async getDocuments(search: string): Promise<DjehootyDocument[]> {
        try {
            var url: string
            if (search.length === 0) {
                url = `${API_BASE_URL}/individuals/${this.id}/documents`
            } else {
                url = `${API_BASE_URL}/individuals/${this.id}/documents?q=${search}`
            }
            const response = await axios.get(url);
            if (!Array.isArray(response.data)) {
                console.warn("API returned no documents or invalid format");
                return [];
            }

            return response.data.map((item: any) => {
                // To be able to call methods from DjehootyFile, we need to instanciate DjehootyFile objects
                const document = new DjehootyDocument();
                document.loadFromJson(item);
                return document;
            });
        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;
            console.error('Error fetching documents - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            // Simplified message for user. We don't expose all technical api errors
            throw new Error("Error fetching documents");
        }
    }

    /**
     * Fetches a list of individuals from the API.
     * @returns Promise<Individual[]>
     */
    static async fetchAll(search: string): Promise<Individual[]> {
        try {
            var url: string
            if (search.length === 0) {
                url = `${API_BASE_URL}/individuals`
            } else {
                url = `${API_BASE_URL}/individuals?q=${search}`
            }
            const response = await axios.get(url);
            if (!Array.isArray(response.data)) {
                console.warn("API returned no individuals or invalid format");
                return [];
            }

            return response.data.map((item: any) => {
                // To be able to call methods from DjehootyFile, we need to instanciate DjehootyFile objects
                const individual = new Individual();
                individual.loadFromJson(item);
                return individual;
            });
        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;
            console.error('Error fetching individuals - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            // Simplified message for user. We don't expose all technical api errors
            throw new Error("Error fetching individuals");
        }
    }

    /**
     * Create an individual
     * @param firstname - Individual firstname(s)
     * @param lastname - Individual lastname
     * @returns Promise resolving to the created Individual.
     * @throws Error if creation fails or response is invalid.
     */
    static async create(updateData: IndividualUpdateData): Promise<Individual> {
        // TODO : Update create with data: IndividualUpdateData
        try {

            // Transform camelCase to snake_case for API
            const apiData: Record<string, any> = {};
            if (updateData.firstname !== undefined) apiData.firstname = updateData.firstname;
            if (updateData.lastname !== undefined) apiData.lastname = updateData.lastname;
            if (updateData.occupation !== undefined) apiData.occupation = updateData.occupation;
            if (updateData.wikipediaUrl !== undefined) apiData.wikipedia_url = updateData.wikipediaUrl;
            if (updateData.wikidataId !== undefined) apiData.wikidata_id = updateData.wikidataId;

            const response = await axios.post(API_BASE_URL + '/individuals', apiData);

            if (response.data && typeof response.data === 'object' && response.data.id) {
                const individual = new Individual();
                individual.loadFromJson(response.data);
                return individual;
            } else {
                // Unexpected response format
                throw new Error("Unexpected response from server during creation operation");
            }
        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;
            console.error('Error creating individual - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            // Propagate error
            throw new Error(apiMessage || "Error creating individual.");
        }
    }

    /**
     * Update an individual
     * @param individualId - Individual id
     * @param updateData - Data to update (at least one field must be provided)
     * @returns Promise<Individual> : the updated individual
     * @throws Error if creation fails or response is invalid.
     */
    static async update(individualId: number, updateData: IndividualUpdateData): Promise<Individual> {
        if (individualId <= 0) {
            throw new Error("Invalid individual ID");
        }

        // Validate that at least one field is provided
        if (Object.keys(updateData).length === 0) {
            throw new Error("No updateData provided for update");
        }
        // Transform camelCase to snake_case for API
        const apiData: Record<string, any> = {};
        if (updateData.firstname !== undefined) apiData.firstname = updateData.firstname;
        if (updateData.lastname !== undefined) apiData.lastname = updateData.lastname;
        if (updateData.occupation !== undefined) apiData.occupation = updateData.occupation;
        if (updateData.wikipediaUrl !== undefined) apiData.wikipedia_url = updateData.wikipediaUrl;
        if (updateData.wikidataId !== undefined) apiData.wikidata_id = updateData.wikidataId;

        try {
            console.log("Updating individual:", individualId, "with data:", apiData);



            const response = await axios.patch(`${API_BASE_URL}/individuals/${individualId}`, apiData);

            if (response.data && typeof response.data === 'object' && response.data.id) {
                const individual = new Individual();
                individual.loadFromJson(response.data);
                return individual;
            } else {
                throw new Error("Unexpected response from server during update operation");
            }
        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;
            console.error('Error updating individual - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            throw new Error(apiMessage || "Error updating individual");
        }
    }

    /**
     * Delete an individual
     * @param individualID - Individual id
     * @returns Promise<void> : 2xx status code is ok, other will throw errors
     * @throws Error if deletion fails or response is invalid.
     */
    static async delete(individualId: number): Promise<void> {
        if (individualId <= 0) {
            throw new Error("Wrong individual ID")
        }

        try {
            // Success is based solely on status code
            // We expect a 204 status code (No content)
            await axios.delete(`${API_BASE_URL}/individuals/${individualId}`);

        } catch (error: any) {

            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;
            console.error('Error deleting individual - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            // Propagate error
            throw new Error(apiMessage || "Error deleting individual.");
        }
    }

    /**
     * Set the file thumbnail for an individual
     * @param individualId - Individual id
     * @param fileThumbnailId - File thumbnail id
     * @returns Promise<void> : 2xx status code is ok, other will throw errors
     * @throws Error if setting thumbnail fails or response is invalid.
     */
    static async setFileThumbnail(individualId: number, fileThumbnailId: number): Promise<void> {
        if (individualId <= 0) {
            throw new Error("Wrong individual ID")
        }
        if (fileThumbnailId <= 0) {
            throw new Error("Wrong file thumbnail ID")
        }

        try {
            await axios.put(`${API_BASE_URL}/individuals/${individualId}/file-thumbnail`, { file_thumbnail_id: fileThumbnailId });
        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;
            console.error('Error setting individual thumbnail - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            // Propagate error
            throw new Error(apiMessage || "Error setting individual thumbnail.");
        }
    }

    /**
     * Unset the file thumbnail for an individual
     * @param individualId - Individual id
     * @returns Promise<void> : 2xx status code is ok, other will throw errors
     * @throws Error if unsetting thumbnail fails or response is invalid.
     */
    static async unsetFileThumbnail(individualId: number): Promise<void> {
        if (individualId <= 0) {
            throw new Error("Wrong individual ID")
        }

        try {
            // Success is based solely on status code
            // We expect a 204 status code (No content)
            await axios.delete(`${API_BASE_URL}/individuals/${individualId}/file-thumbnail`);

        } catch (error: any) {

            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;
            console.error('Error unsetting individual thumbnail - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            // Propagate error
            throw new Error(apiMessage || "Error unsetting individual thumbnail.");
        }
    }
}